import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';
import type { ConnectionDto } from '../generated/contracts';
import { ArrowModes, LineStyles, toArrowMode, toLineStyle } from '../net/wire';
import { orientBetween } from './NodeMeshFactory';

const DEFAULT_COLOR = '#B0B7C3';

/**
 * Halved from 0.012 after an owner report: with the M9 arrowhead-scaling bug fixed elsewhere
 * (arrowheads now really are ARROW_LENGTH=0.07 long, ARROW_DIAMETER=0.045 across), a 24mm-thick
 * tube still crowded the arrowhead — the two were close enough in width that the cone read as a
 * thick spot on the line rather than a distinct arrowhead. Halving the tube leaves it a bit
 * under a third of the arrowhead's own diameter, wide enough to read as a line from across the
 * room and thin enough that the cone clearly flares out from it.
 *
 * Independent of edge-selection tolerance by construction: EDGE_AIM_TOLERANCE in
 * ConnectionDrawer.ts is a flat constant compared against distance from the aim ray to the
 * edge's *centreline* samples, never against this radius or anything derived from it — the two
 * files don't reference each other's constants at all. So thinning the tube changes only what
 * is drawn, not what can be pointed at; see ConnectionDrawer.test.ts for the behavioural proof.
 */
const EDGE_RADIUS = 0.006;

/** How far a selected edge is thickened, so selection is visible without changing its route. */
const SELECTED_SCALE = 2.2;

const ARROW_LENGTH = 0.07;
const ARROW_DIAMETER = 0.045;

/** Dash length in metres. Repeated along the tube by scaling the texture, not the geometry. */
const DASH_PERIOD = 0.09;

/**
 * Dot spacing in metres — tighter than DASH_PERIOD, and paired with a much smaller opaque
 * fraction (see buildStripe's DOT_DUTY_FRACTION), so a dotted edge reads as a string of small
 * marks rather than a coarser dashed line at a glance.
 */
const DOT_PERIOD = 0.035;

/** Fraction of one stripe period that is opaque. Dashed reuses the texture's own historical 9/16. */
const DASH_DUTY_FRACTION = 9 / 16;

/** Well under half of DASH_DUTY_FRACTION, so the two stripes are visibly distinct textures. */
const DOT_DUTY_FRACTION = 3 / 16;

/**
 * Draws diagram edges as thin tubes rather than lines.
 *
 * A line primitive has no thickness, which in stereo makes it read as an artefact at the
 * wrong depth instead of an object in the room. A tube has real geometry, so both eyes
 * agree on where it is.
 *
 * An edge carries three things beyond its route: which ends have arrowheads, whether the line is
 * solid or dashed, and a colour. Direction and dash are what make this useful for architecture —
 * which way it flows, and whether the call is synchronous.
 */
export class ConnectionRenderer {
  private readonly edges = new Map<string, Edge>();

  private selectedId: string | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly parent: TransformNode,
  ) {}

  /**
   * Creates or updates the edge. Endpoints are passed in rather than looked up, because
   * while a node is held its live position comes from the transform stream, not the DTO.
   *
   * `toRadius`/`fromRadius` inset each end's arrowhead so it sits against its own node rather
   * than buried inside it — the tube may end at a node's centre, but an arrow must not. Each
   * end is inset by its own node's radius: `both` mode draws one arrowhead facing into each
   * node, and sizing both from a single shared radius put the wrong inset on whichever end's
   * node did not happen to match it.
   */
  render(connection: ConnectionDto, from: Vector3, to: Vector3, toRadius = 0, fromRadius = 0): void {
    const edge = this.edges.get(connection.id) ?? this.create(connection.id);
    const arrow = toArrowMode(connection.style.arrow);
    const line = toLineStyle(connection.style.line);
    const color = Color3.FromHexString(connection.style.color ?? DEFAULT_COLOR);

    this.paint(edge, color, line, from, to);

    const selected = this.selectedId === connection.id;
    const thickness = selected ? SELECTED_SCALE : 1;

    orientBetween(edge.tube, from, to);
    edge.tube.scaling.x = thickness;
    edge.tube.scaling.z = thickness;

    const direction = to.subtract(from);
    const length = direction.length();
    const unit = length < 1e-6 ? Vector3.Up() : direction.scale(1 / length);

    // Forward points at the target; both adds one facing back at the source.
    this.placeArrow(
      edge,
      'forward',
      arrow !== ArrowModes.None,
      to.subtract(unit.scale(toRadius)),
      unit,
      color,
      thickness,
    );

    this.placeArrow(
      edge,
      'backward',
      arrow === ArrowModes.Both,
      from.add(unit.scale(fromRadius)),
      unit.scale(-1),
      color,
      thickness,
    );
  }

  /**
   * Marks one edge as selected, or clears the selection.
   *
   * Thickening rather than recolouring: an edge's colour is authored content, and overwriting it
   * to show selection would make a red edge indistinguishable from a selected one.
   */
  select(connectionId: string | null): void {
    this.selectedId = connectionId;
  }

  get selected(): string | null {
    return this.selectedId;
  }

  /**
   * Found while building Link mode's own live style preview (`LinkPreviewRenderer.ts`), which
   * reuses this exact method to tear its stub down on every mode change and would otherwise leak
   * a `StandardMaterial` on every single tick a style with an arrowhead was shown: `edge.forward`/
   * `edge.backward` each carry their own per-cone material, built fresh in `placeArrow` (`cone.material
   * = material`, never shared with the tube's), but this method used to dispose only `edge.tube`'s
   * material before disposing the two cone meshes with no arguments — `AbstractMesh.dispose()`'s
   * second argument, `disposeMaterialAndTextures`, defaults to `false` (CLAUDE.md, "`AbstractMesh.dispose()`
   * ... does not free materials"), so each cone's own material was left alive, un-disposed and
   * unreachable, every time an edge with a real arrowhead was removed — including in ordinary
   * production use (`RoomView`'s own `connections.remove`, whenever a connection with an
   * arrowhead is deleted), not only this preview's own per-tick rebuild. Disposing each cone's
   * material explicitly, the same way the tube's own material already was, is the fix; the tube
   * and both cones now free identically.
   */
  remove(connectionId: string): void {
    const edge = this.edges.get(connectionId);

    if (!edge) {
      return;
    }

    edge.dash?.dispose();
    edge.tube.material?.dispose();
    edge.tube.dispose();
    edge.forward?.material?.dispose();
    edge.forward?.dispose();
    edge.backward?.material?.dispose();
    edge.backward?.dispose();
    this.edges.delete(connectionId);

    if (this.selectedId === connectionId) {
      this.selectedId = null;
    }
  }

  has(connectionId: string): boolean {
    return this.edges.has(connectionId);
  }

  clear(): void {
    for (const id of [...this.edges.keys()]) {
      this.remove(id);
    }
  }

  private create(connectionId: string): Edge {
    // A unit-height cylinder scaled along Y; orientBetween does the rest.
    const tube = CreateCylinder(
      `edge-${connectionId}`,
      { height: 1, diameter: EDGE_RADIUS * 2, tessellation: 6 },
      this.scene,
    );

    const material = new StandardMaterial(`edge-${connectionId}`, this.scene);
    material.specularColor = Color3.Black();
    tube.material = material;

    // Edges are scenery to the hand: picking them would compete with grabbing the nodes they
    // join. Selecting one is done by aim in ConnectionDrawer, which does its own maths.
    tube.isPickable = false;
    tube.parent = this.parent;

    const edge: Edge = { tube, forward: null, backward: null, dash: null, lineStyle: LineStyles.Solid };
    this.edges.set(connectionId, edge);

    return edge;
  }

  private paint(edge: Edge, color: Color3, line: LineStyleValue, from: Vector3, to: Vector3): void {
    const material = edge.tube.material;

    if (!(material instanceof StandardMaterial)) {
      return;
    }

    material.diffuseColor = color;
    material.emissiveColor = color.scale(0.4);

    if (line !== edge.lineStyle) {
      material.opacityTexture = null;
      edge.dash?.dispose();
      edge.dash = null;

      if (line === LineStyles.Dashed) {
        edge.dash = this.buildStripe(DASH_DUTY_FRACTION);
      } else if (line === LineStyles.Dotted) {
        edge.dash = this.buildStripe(DOT_DUTY_FRACTION);
      }

      material.opacityTexture = edge.dash;
      edge.lineStyle = line;
    }

    // Scaled to the edge's length so a dash/dot is the same size on a long edge as a short one.
    // Cheaper than cutting the tube into N cylinders, which would multiply draw calls per edge.
    if (edge.dash) {
      const period = edge.lineStyle === LineStyles.Dashed ? DASH_PERIOD : DOT_PERIOD;
      edge.dash.vScale = Math.max(1, Math.round(Vector3.Distance(from, to) / period));
    }
  }

  private placeArrow(
    edge: Edge,
    end: 'forward' | 'backward',
    wanted: boolean,
    tip: Vector3,
    direction: Vector3,
    color: Color3,
    thickness: number,
  ): void {
    let cone = edge[end];

    if (!wanted) {
      cone?.setEnabled(false);
      return;
    }

    if (!cone) {
      // Unit height, matching the tube: orientBetween (below) scales a mesh's own Y axis to
      // the span between the two points it is given, on the assumption the mesh is already
      // unit-height and that scale *is* the final size. Building this at its real ARROW_LENGTH
      // instead applied that scale a second time — ARROW_LENGTH^2, about 5mm instead of 7cm,
      // rather than the arrowhead nobody could actually see it as from four metres away.
      cone = CreateCylinder(
        `${edge.tube.name}-${end}`,
        { height: 1, diameterTop: 0, diameterBottom: ARROW_DIAMETER, tessellation: 8 },
        this.scene,
      );

      const material = new StandardMaterial(`${edge.tube.name}-${end}`, this.scene);
      material.specularColor = Color3.Black();
      cone.material = material;
      cone.isPickable = false;
      cone.parent = this.parent;

      edge[end] = cone;
    }

    const material = cone.material;
    if (material instanceof StandardMaterial) {
      material.diffuseColor = color;
      material.emissiveColor = color.scale(0.4);
    }

    cone.setEnabled(true);

    // orientBetween aligns a unit-height mesh along a segment, so the cone is described as the
    // short segment from its base to its tip rather than by an angle.
    orientBetween(cone, tip.subtract(direction.scale(ARROW_LENGTH)), tip);
    cone.scaling.x = thickness;
    cone.scaling.z = thickness;
  }

  /**
   * A stripe used as an opacity mask: opaque for `dutyFraction` of a period, transparent for the
   * rest. Dashed and dotted share this construction and differ only in how much of the period is
   * opaque — a long, roughly-half-open stripe reads as a dash, a short one reads as a dot.
   *
   * Built fresh per edge rather than drawn once and shared via `DynamicTexture.clone()` — that
   * was the previous approach (one shared "source" texture, cloned per dashed edge because
   * `vScale` lives on the texture, not the material) and it does not do what it looks like it
   * does: `DynamicTexture.clone()` constructs a brand-new canvas of the same size and copies
   * none of the pixels drawn on the original (confirmed by reading
   * `@babylonjs/core/Materials/Textures/dynamicTexture.js`'s own `clone()`, and directly, by
   * drawing on a texture, cloning it, and finding the clone's own 2D context had recorded zero
   * draw calls). Every dashed edge was therefore getting an undrawn, blank opacity mask — sixteen
   * texels of whatever a freshly constructed canvas defaults to, not the intended stripe.
   * Sixteen texels and two `fillRect` calls cost nothing worth measuring, so building the real
   * thing per edge is both correct and no more expensive than the broken shortcut was believed to
   * be.
   */
  private buildStripe(dutyFraction: number): DynamicTexture {
    const height = 16;
    const opaqueTexels = Math.round(height * dutyFraction);

    const texture = new DynamicTexture(
      `edge-stripe-${dutyFraction}`,
      { width: 1, height },
      this.scene,
      false,
    );
    const context = as2d(texture.getContext());

    context.fillStyle = '#000000';
    context.fillRect(0, 0, 1, height);
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, 1, opaqueTexels);

    texture.update();
    texture.wrapV = DynamicTexture.WRAP_ADDRESSMODE;
    texture.getAlphaFromRGB = true;

    return texture;
  }
}

type LineStyleValue = ReturnType<typeof toLineStyle>;

interface Edge {
  tube: Mesh;
  forward: Mesh | null;
  backward: Mesh | null;
  dash: DynamicTexture | null;
  lineStyle: LineStyleValue;
}
